import { UploadAction } from "enums/uploadMode";
import * as hooks from "lib/hooks";
import { Containment } from "models";
import {
	OBJECT_ID_ASRS_LOCK,
	OBJECT_ID_ASRS_UNLOCK,
	OBJECT_ID_CREATE_CONTAINMENT,
} from "pages/Users/constants";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { RootState, SelectContainmentById } from "store";

export function UseUploadPageAccess(
	action: UploadAction | undefined,
	containmentId?: string
): {
	hasUploadPageAccess: boolean;
	isCheckingUploadPageAccess: boolean;
	containmentLoadFailed: boolean;
} {
	const permissions = hooks.UseHasPermission();
	const api = hooks.UseMccApi();
	const containmentFromStore = useSelector((s: RootState) =>
		SelectContainmentById(s, containmentId)
	);
	const storeContainment = containmentFromStore.UnwrapOrNil();
	const [fetchedContainment, setFetchedContainment] = useState<Containment>();
	const [containmentLoadFailed, setContainmentLoadFailed] = useState(false);

	useEffect(() => {
		setFetchedContainment(undefined);
		setContainmentLoadFailed(false);

		if (!containmentId || storeContainment) {
			return;
		}

		let cancelled = false;

		api.ContainmentById(containmentId)
			.then((containment) => {
				if (cancelled) {
					return;
				}
				const resolved = containment.UnwrapOrNil();
				setFetchedContainment(resolved);
				setContainmentLoadFailed(!resolved);
			})
			.catch(() => {
				if (!cancelled) {
					setFetchedContainment(undefined);
					setContainmentLoadFailed(true);
				}
			});

		return () => {
			cancelled = true;
		};
	}, [api, containmentId, storeContainment]);

	return useMemo(() => {
		if (!containmentId) {
			return {
				hasUploadPageAccess:
					permissions.createContainmentPlantIds.length > 0 ||
					permissions.asrsLockPlantIds.length > 0,
				isCheckingUploadPageAccess: false,
				containmentLoadFailed: false,
			};
		}

		if (containmentLoadFailed) {
			return {
				hasUploadPageAccess: false,
				isCheckingUploadPageAccess: false,
				containmentLoadFailed: true,
			};
		}

		const containment = storeContainment ?? fetchedContainment;
		if (!containment) {
			return {
				hasUploadPageAccess: false,
				isCheckingUploadPageAccess: true,
				containmentLoadFailed: false,
			};
		}

		const checkContainmentPermission = (objectId: string) =>
			permissions.hasPermission(containment, objectId);

		switch (action) {
			case UploadAction.Unlock:
				return {
					hasUploadPageAccess: checkContainmentPermission(OBJECT_ID_ASRS_UNLOCK),
					isCheckingUploadPageAccess: false,
					containmentLoadFailed: false,
				};
			case UploadAction.Lock:
				return {
					hasUploadPageAccess: checkContainmentPermission(OBJECT_ID_ASRS_LOCK),
					isCheckingUploadPageAccess: false,
					containmentLoadFailed: false,
				};
			default:
				return {
					hasUploadPageAccess: checkContainmentPermission(OBJECT_ID_CREATE_CONTAINMENT),
					isCheckingUploadPageAccess: false,
					containmentLoadFailed: false,
				};
		}
	}, [
		action,
		containmentId,
		containmentLoadFailed,
		fetchedContainment,
		permissions,
		storeContainment,
	]);
}
